import { useEffect, useState, useRef, CSSProperties } from "react";
import { createPortal } from "react-dom";

type PopperPlacement = 
    | "top-start" | "top" | "top-end"
    | "right-start" | "right" | "right-end"
    | "bottom-start" | "bottom" | "bottom-end"
    | "left-start" | "left" | "left-end";

type PopperProps<T extends HTMLElement = HTMLElement> = {
    targetRef: { readonly current: T | null };
    placement?: PopperPlacement;
    isOpen: boolean;
    onClose?: () => void;
    closeOnOutsideClick?: boolean;
    closeOnEscape?: boolean;
    zIndex?: number;
    className?: string;
    style?: CSSProperties;
    children: React.ReactNode;
};

function Popper<T extends HTMLElement = HTMLElement>({
    targetRef,
    placement = "bottom-start",
    isOpen,
    onClose,
    closeOnOutsideClick = true,
    closeOnEscape = true,
    zIndex = 1000,
    className = "",
    style,
    children,
}: PopperProps<T>) {
    const popperRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const offset = 8;

    // 위치 계산
    useEffect(() => {
        if (!isOpen || !targetRef.current || !popperRef.current) return;

        const updatePosition = () => {
            const target = targetRef.current!;
            const popper = popperRef.current!;

            const targetRect = target.getBoundingClientRect();
            const popperRect = popper.getBoundingClientRect();

            const positions = calculatePosition(
                targetRect,
                popperRect,
                placement,
                offset
            );

            setPosition(positions);
        };

        updatePosition();

        // 스크롤/리사이즈 시 위치 재계산
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [isOpen, targetRef, placement, offset]);

    // 외부 클릭 감지
    useEffect(() => {
        if (!isOpen || !closeOnOutsideClick) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            
            if (
                popperRef.current &&
                !popperRef.current.contains(target) &&
                targetRef.current &&
                !targetRef.current.contains(target)
            ) {
                onClose?.();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen, closeOnOutsideClick, onClose, targetRef]);

    // ESC 키 감지
    useEffect(() => {
        if (!isOpen || !closeOnEscape) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose?.();
            }
        };

        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
    }, [isOpen, closeOnEscape, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div
            ref={popperRef}
            className={className}
            style={{
                position: "fixed",
                top: position.top,
                left: position.left,
                zIndex,
                ...style,
            }}
        >
            {children}
        </div>,
        document.body
    );
}

/** 위치 계산 함수 */
function calculatePosition(
    targetRect: DOMRect,
    popperRect: DOMRect,
    placement: PopperPlacement,
    offset: number
): { top: number; left: number } {
    let top = 0;
    let left = 0;

    // 기본 위치 계산
    switch (placement) {
        // Top
        case "top-start":
            top = targetRect.top - popperRect.height - offset;
            left = targetRect.left;
            break;
        case "top":
            top = targetRect.top - popperRect.height - offset;
            left = targetRect.left + targetRect.width / 2 - popperRect.width / 2;
            break;
        case "top-end":
            top = targetRect.top - popperRect.height - offset;
            left = targetRect.right - popperRect.width;
            break;

        // Right
        case "right-start":
            top = targetRect.top;
            left = targetRect.right + offset;
            break;
        case "right":
            top = targetRect.top + targetRect.height / 2 - popperRect.height / 2;
            left = targetRect.right + offset;
            break;
        case "right-end":
            top = targetRect.bottom - popperRect.height;
            left = targetRect.right + offset;
            break;

        // Bottom
        case "bottom-start":
            top = targetRect.bottom + offset;
            left = targetRect.left;
            break;
        case "bottom":
            top = targetRect.bottom + offset;
            left = targetRect.left + targetRect.width / 2 - popperRect.width / 2;
            break;
        case "bottom-end":
            top = targetRect.bottom + offset;
            left = targetRect.right - popperRect.width;
            break;

        // Left
        case "left-start":
            top = targetRect.top;
            left = targetRect.left - popperRect.width - offset;
            break;
        case "left":
            top = targetRect.top + targetRect.height / 2 - popperRect.height / 2;
            left = targetRect.left - popperRect.width - offset;
            break;
        case "left-end":
            top = targetRect.bottom - popperRect.height;
            left = targetRect.left - popperRect.width - offset;
            break;
    }

    // 화면 경계 체크 및 조정
    const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
    };

    // 오른쪽 경계 초과
    if (left + popperRect.width > viewport.width) {
        left = viewport.width - popperRect.width - 8;
    }
    // 왼쪽 경계 초과
    if (left < 0) {
        left = 8;
    }
    // 아래쪽 경계 초과
    if (top + popperRect.height > viewport.height) {
        top = viewport.height - popperRect.height - 8;
    }
    // 위쪽 경계 초과
    if (top < 0) {
        top = 8;
    }

    return { top, left };
}

export default Popper;